// ─── WhimerComposer — hybrid input shared by WhimerClean & WhimerTerm ─────────────
//
// compose mode: rich edit + image attach → onSubmit (paste-safe submit).
// passthrough mode (draft starts with `/`): keystrokes are forwarded to the PTY so the
// agent shows its native command suggestions; Enter runs it. Deleting the `/` returns
// to compose. This is the "ผสม" the user asked for — clean-input flourishes AND real
// terminal `/` commands.

import { useState, useEffect, useRef } from 'react'
import { Icon, type IconName } from '../Icon'
import { useDraft } from '../../hooks/useDraft'
import { useVoice } from '../../hooks/useVoice'
import { useFileAttach, AttachmentBar, buildMessageWithAttachments } from '../../plugins/imageAttach'
import { useWindowDrop } from '../../hooks/useWindowDrop'
import { IS_MOBILE } from '../../constants/config'
import { listComposerTools } from '../../composerTools'
import { getSlotContributions } from '../../slots'
import { renderSlotNode } from '../../slots/WidgetView'
import type { SlotContext, PaneState } from '../../slots/types'

interface ComposerProps {
  chatId: string
  senderLabel: string
  disabled?: boolean
  /** terminal skin (WhimerTerm) vs clean skin (WhimerClean) — affects placeholder copy only. */
  terminal?: boolean
  onSubmit: (content: string) => void
  sendInput: (data: string) => void
  /** Toggle the parent's live-voice (Gemini) panel — rendered as a tool button when given. */
  onToggleVoice?: () => void
  voiceActive?: boolean
  /** ESC recall (terminal parity): return the last still-pending message's text to pull back
   *  into the box (and let the view drop its optimistic turn), or null if nothing to recall. */
  onRecall?: () => string | null
  /** Active chat's agent + pane state — passed to composer-tool slot contributions' `when`/ctx. */
  agent?: string
  paneState?: PaneState
}

export function WhimerComposer({ chatId, senderLabel, disabled, terminal, onSubmit, sendInput, onToggleVoice, voiceActive, onRecall, agent, paneState }: ComposerProps) {
  // Draft persists per chat (survives chat switch & view-mode switch remounts).
  const [text, setText] = useDraft(chatId)
  const [passthrough, setPassthrough] = useState(false)
  const [appsOpen, setAppsOpen] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const extTools = listComposerTools() // external/community composer-tool plugins (apps popup)
  const { attachments, addFiles, removeAttachment, clear, uploading, ready } = useFileAttach(chatId)
  const { dragging: windowDragging, overlayLeave } = useWindowDrop()
  // Voice dictation (Web Speech API) — fills the draft so you can review then send.
  const { isListening, isSupported, startListening, stopListening } = useVoice({
    onTranscript: (t) => setText(prev => (prev ? `${prev} ${t}` : t).trim()),
  })

  useEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'
  }, [text])

  const exitPassthrough = () => { setPassthrough(false); setText('') }

  const submitCompose = () => {
    const t = text.trim()
    if (uploading || disabled) return
    if (!t && ready.length === 0) return
    onSubmit(buildMessageWithAttachments(t, ready))
    setText(''); clear()
    requestAnimationFrame(() => { const el = taRef.current; if (el) el.style.height = 'auto' })
  }

  // ESC / stop: interrupt the agent (send ESC to the PTY) AND — only when the box is empty —
  // pull the last still-pending message back in to edit/resend (terminal parity). Never clobbers
  // a draft you're mid-typing; never touches an already-committed transcript turn (onRecall only
  // returns pending optimistic ones). Without onRecall (legacy views) it's a plain interrupt.
  const handleStop = () => {
    sendInput('\x1b')
    if (onRecall && !passthrough && !text.trim()) {
      const recalled = onRecall()
      if (recalled) { setText(recalled); requestAnimationFrame(() => taRef.current?.focus()) }
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const composing = e.nativeEvent.isComposing || e.keyCode === 229
    if (composing) return

    if (passthrough) {
      // Forward keystrokes to the agent so its `/` menu lives + updates.
      if (e.key === 'Enter')      { e.preventDefault(); sendInput('\r'); exitPassthrough(); return }
      if (e.key === 'Escape')     { e.preventDefault(); sendInput('\x1b'); exitPassthrough(); return }
      if (e.key === 'ArrowUp')    { e.preventDefault(); sendInput('\x1b[A'); return }
      if (e.key === 'ArrowDown')  { e.preventDefault(); sendInput('\x1b[B'); return }
      if (e.key === 'Tab')        { e.preventDefault(); sendInput('\t'); setText(t => t + '\t'); return }
      if (e.key === 'Backspace')  {
        e.preventDefault(); sendInput('\x7f')
        const next = text.slice(0, -1); setText(next)
        if (!next.startsWith('/')) setPassthrough(false)
        return
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault(); sendInput(e.key); setText(t => t + e.key); return
      }
      return
    }

    // ESC (compose): interrupt + recall the last in-flight message into the box (terminal parity).
    if (e.key === 'Escape' && onRecall) { e.preventDefault(); handleStop(); return }
    // compose → entering a `/` command on an empty line flips to passthrough.
    if (text === '' && e.key === '/') {
      e.preventDefault(); setPassthrough(true); setText('/'); sendInput('/'); return
    }
    if (e.key === 'Enter' && !IS_MOBILE && !e.shiftKey) { e.preventDefault(); submitCompose() }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (passthrough) return
    const files = Array.from(e.clipboardData?.files || [])
    if (files.length) { e.preventDefault(); addFiles(files) }
  }

  const sendDisabled = (!text.trim() && ready.length === 0) || uploading || !!disabled
  const placeholder = passthrough
    ? 'พิมพ์คำสั่ง /…'
    : terminal
      ? `$ message or / command…`
      : `Message ${senderLabel}...  (พิมพ์ / เพื่อสั่ง terminal)`

  return (
    <>
    {windowDragging && (
      <div
        className="drop-overlay"
        onDragOver={e => e.preventDefault()}
        onDragLeave={overlayLeave}
        onDrop={e => { e.preventDefault(); overlayLeave(); const files = Array.from(e.dataTransfer?.files ?? []); if (files.length) addFiles(files) }}
      >
        <div className="drop-overlay__inner">
          <Icon name="upload-cloud" size={48} />
          <div>วางไฟล์เพื่อแนบในแชท</div>
        </div>
      </div>
    )}
    <div className={`chat-composer whimer-composer${terminal ? ' whimer-composer--term' : ''}${passthrough ? ' whimer-composer--passthrough' : ''}`}>
      {passthrough && (
        <div className="whimer-passthrough-tag">
          <Icon name="terminal" size={13} /> ส่งตรงเข้า terminal — เมนู / ของ agent กำลังทำงาน (Esc เพื่อออก)
        </div>
      )}
      <AttachmentBar attachments={attachments} onRemove={removeAttachment} />
      <textarea
        ref={taRef}
        className="chat-input-textarea"
        value={text}
        onChange={e => { if (!passthrough) setText(e.target.value) }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        rows={1}
        disabled={disabled}
      />
      <input ref={fileRef} type="file" multiple hidden
        onChange={e => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = '' }} />
      <div className="chat-composer-actions">
        <div className="chat-composer-tools">
          <button className="chat-tool-btn chat-tool-btn--cancel" type="button"
            title={onRecall ? 'ยกเลิก / หยุด (ESC) · กล่องว่าง = ดึงข้อความล่าสุดกลับมาแก้' : 'ยกเลิก / หยุด (ส่ง ESC)'}
            aria-label="ยกเลิก หรือ หยุดการทำงาน (ส่ง ESC)"
            disabled={disabled} onClick={handleStop}>
            <Icon name="stop" size={18} />
          </button>
          <button className="chat-tool-btn" type="button" title="Attach file or image" aria-label="Attach file or image"
            disabled={disabled || passthrough} onClick={() => fileRef.current?.click()}>
            <Icon name="paperclip" size={18} />
          </button>
          {isSupported && (
            <button className={`chat-tool-btn${isListening ? ' chat-tool-btn--active' : ''}`} type="button"
              title={isListening ? 'หยุดพิมพ์ด้วยเสียง' : 'พิมพ์ด้วยเสียง'} aria-label="Dictate" aria-pressed={isListening}
              disabled={disabled} onClick={() => (isListening ? stopListening() : startListening())}>
              <Icon name="mic" size={18} />
            </button>
          )}
          {onToggleVoice && (
            <button className={`chat-tool-btn${voiceActive ? ' chat-tool-btn--active' : ''}`} type="button"
              title={voiceActive ? 'ปิด voice panel' : 'Live voice (Gemini)'} aria-label="Live voice" aria-pressed={!!voiceActive}
              disabled={disabled} onClick={onToggleVoice}>
              <Icon name="phone" size={18} />
            </button>
          )}
          {/* "apps" — external/community composer-tool plugins (internal tools stay inline above).
              Empty today; the popup lists whatever the composer-tool registry contributes. */}
          <div className="chat-composer-apps">
            <button className={`chat-tool-btn${appsOpen ? ' chat-tool-btn--active' : ''}`} type="button"
              title="ปลั๊กอินเสริม" aria-label="ปลั๊กอินเสริม (apps)" aria-expanded={appsOpen}
              disabled={disabled} onClick={() => setAppsOpen((o) => !o)}>
              <Icon name="grid" size={18} />
            </button>
            {appsOpen && (() => {
              // C2 — composer-tool slot: declarative/plugin contributions render in the apps popup
              // alongside the legacy composerTools registry. SlotContext doubles as the legacy
              // ComposerToolContext (superset), so both paths share one ctx.
              const slotCtx: SlotContext = {
                chatId, agent: agent ?? '', paneState: paneState ?? 'idle',
                sendInput, submit: onSubmit, close: () => setAppsOpen(false),
              }
              const toolSlots = getSlotContributions('composer-tool', slotCtx)
              const empty = extTools.length === 0 && toolSlots.length === 0
              return (
                <>
                  <div className="chat-apps-backdrop" onClick={() => setAppsOpen(false)} />
                  <div className="chat-apps-menu" role="menu">
                    {empty ? (
                      <div className="chat-apps-empty">ยังไม่มีปลั๊กอินเสริม</div>
                    ) : (
                      <>
                        {extTools.map((t) => (
                          t.render
                            ? <div className="chat-apps-custom" key={t.id}>{t.render(slotCtx)}</div>
                            : (
                              <button key={t.id} className="chat-apps-item" type="button" role="menuitem"
                                onClick={() => { setAppsOpen(false); t.onSelect?.(slotCtx) }}>
                                <Icon name={t.icon as IconName} size={16} /> <span>{t.title}</span>
                              </button>
                            )
                        ))}
                        {toolSlots.map((c) => (
                          <div className="chat-apps-custom" key={c.id}>{renderSlotNode(c, slotCtx)}</div>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )
            })()}
          </div>
        </div>
        <div className="chat-composer-send-group">
          <button className="chat-send-btn" type="button" onClick={submitCompose}
            disabled={passthrough ? false : sendDisabled}
            title={passthrough ? 'กด Enter เพื่อรันคำสั่ง' : (uploading ? 'Uploading...' : 'Send')}
            onMouseDown={e => { if (passthrough) { e.preventDefault(); sendInput('\r'); exitPassthrough() } }}>
            <Icon name="send" size={18} />
          </button>
        </div>
      </div>
    </div>
    </>
  )
}
